import useAxios from "./../../../Hooks/useAxios";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";

// Icons
import {
  FiPhone,
  FiMail,
  FiUser,
  FiHash,
  FiCalendar,
  FiGlobe,
  FiMapPin,
} from "react-icons/fi";
import { MdLocationCity } from "react-icons/md";

function PatientInfo() {
  const params = useParams();
  const [patient, setPatient] = useState(null);
  const request = useAxios();

  const safe = (value) => value ?? "—"; // Use ?? to catch null and undefined

  // Calculate age safely from DOB
  const calculateAge = (dob) => {
    if (!dob) return "—";
    const birthDate = new Date(Number(dob));
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age;
  };

  const formatDate = (date) => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getPatient = async () => {
    const res = await request({
      method: "GET",
      url: "chapchap/abcd/patients",
      params: { id: params.id },
      showSuccessModal: false,
    });

    if (res._error) return;
    setPatient(res?.data ?? {});
  };

  useEffect(() => {
    getPatient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!patient) {
    return (
      <div className="p-5 text-center text-auro_metal_saurus">
        Loading patient information...
      </div>
    );
  }

  return (
    <div className="w-full p-5 flex justify-center">
      <div className="bg-white shadow-xl rounded-3xl p-8 border border-bright_grey  w-full">
        {/* HEADER */}
        <div className="flex items-center gap-6 border-b border-bright_grey pb-6 mb-6">
          <div className="w-24 h-24 rounded-full bg-ghost_white flex items-center justify-center text-5xl text-primary font-bold shadow-inner">
            {safe(patient?.first_name?.[0] || "?")}
          </div>

          <div>
            <h1 className="text-3xl font-bold text-yankees_blue tracking-wide">
              {safe(patient?.first_name)} {safe(patient?.second_name)}{" "}
              {safe(patient?.sir_name)}
            </h1>

            <p className="text-auro_metal_saurus mt-1 flex items-center gap-1">
              <FiHash /> Patient ID: {safe(patient?.id)}
            </p>

            <p className="text-auro_metal_saurus mt-1 flex items-center gap-1">
              <FiCalendar /> Created At: {formatDate(patient?.created_at)}
            </p>
          </div>
        </div>

        {/* DETAILS GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <Detail
              icon={<FiPhone />}
              label="Phone"
              value={safe(patient?.phone)}
            />
            <Detail
              icon={<FiMail />}
              label="Email"
              value={safe(patient?.email)}
            />
            <Detail
              icon={<FiHash />}
              label="ID Number"
              value={safe(patient?.id_no)}
            />
            <Detail
              icon={<FiCalendar />}
              label="Date of Birth"
              value={
                patient?.dob
                  ? new Date(Number(patient?.dob)).toDateString()
                  : "—"
              }
            />
            <Detail
              icon={<FiUser />}
              label="Age"
              value={calculateAge(patient?.dob)}
            />
          </div>

          <div className="space-y-3">
            <Detail
              icon={<FiUser />}
              label="Gender"
              value={safe(patient?.gender)}
            />
            <Detail
              icon={<FiGlobe />}
              label="Nationality"
              value={safe(patient?.nationality)}
            />
            <Detail
              icon={<FiUser />}
              label="Religion"
              value={safe(patient?.religion)}
            />
            <Detail
              icon={<FiUser />}
              label="Occupation"
              value={safe(patient?.occupation)}
            />
          </div>
        </div>

        {/* ADDRESS */}
        <Section title="Location">
          <Detail
            icon={<MdLocationCity />}
            label="District"
            value={safe(patient?.district)}
          />
          <Detail
            icon={<FiMapPin />}
            label="Ward"
            value={safe(patient?.ward)}
          />
        </Section>

        {/* RECEPTIONIST */}
        <Section title="Patient Added By">
          <Detail
            icon={<FiUser />}
            label="Name"
            value={safe(patient?.team?.name)}
          />
          <Detail
            icon={<FiMail />}
            label="Email"
            value={safe(patient?.team?.email)}
          />
          <Detail
            icon={<FiPhone />}
            label="Phone"
            value={safe(patient?.team?.phone)}
          />
        </Section>
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="mt-8 p-5 bg-ghost_white rounded-2xl shadow-sm border border-bright_grey">
    <h2 className="text-xl font-semibold text-yankees_blue mb-4">{title}</h2>
    <div className="space-y-2">{children}</div>
  </div>
);

const Detail = ({ label, value, icon }) => (
  <div className="flex justify-between items-center py-2 px-3 bg-white rounded-xl shadow border border-bright_grey">
    <div className="flex items-center gap-2 text-auro_metal_saurus">
      <span className="text-primary text-lg">{icon}</span>
      <span className="font-medium">{label}</span>
    </div>

    <span className="text-yankees_blue font-semibold">{value}</span>
  </div>
);

export default PatientInfo;
